
import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
import { apiSignIn, apiSignUp, apigetUserDetails, apiLogout } from "services/AuthService";
import {
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
  clearAuthTokens,
} from "utils/authTokens";
import { resetRefreshState } from "services/TokenRefreshService";
import { toast, Notification } from "components/ui";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const { token, signedIn } = useSelector((state) => state.auth.session);

  const signIn = async (values) => {
    try {
      const resp = await apiSignIn(values);
      if (resp.data) {
        const { token, refreshToken } = resp.data;
        dispatch(onSignInSuccess(token));
        setAccessToken(token);
        // The refresh token is what keeps the session alive once the short-lived
        // access token expires. Without it the user would be signed out ~15
        // minutes in — possibly mid-invoice.
        setRefreshToken(refreshToken);
        if (
          resp.status === 200 &&
          resp.data.status !== "failed" &&
          resp.data.status !== "Failed" &&
          resp.data.status !== "Error"
        ) {
         

          const getUserData = async () => {
            try {
              const response = await apigetUserDetails(resp.data.userId);
              if (response.data && response.status === 200) {
                localStorage.setItem(
                  "userDetails",
                  JSON.stringify(response.data)
                );
                const redirectUrl = query.get(REDIRECT_URL_KEY);
                navigate(
                  redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                );
                if (
                  resp.status === 200 &&
                  resp.data.status !== "failed" &&
                  resp.data.status !== "Failed"
                ) {
                  toast.push(
                    <Notification
                      title={"Sign In Successful"}
                      type="success"
                      duration={5000}
                    >
                      You have successfully signed in.
                    </Notification>,
                    { placement: "top-center" }
                  );
                }
                window.location.reload();
              }
            } catch (errors) {
              return {
                status: "failed",
                message: errors?.response?.data?.message || errors.toString(),
                errors: errors?.response?.data?.status,
              };
            }
          };
          await getUserData();
        } else {
          toast.push(
            <Notification
              title={"Sign in Failed"}
              type="warning"
              duration={5000}
            >
              {resp.data.message || resp.data.response}
            </Notification>,
            { placement: "top-center" }
          );
          signOut();
          navigate("/sign-in");
        }
      }
    } catch (error) {
      if (error?.response?.status !== 200) {
        signOut();
        navigate("/sign-in");
      }
      let _error_res = "";
      if (error.response) {
        if (error.response.data.message) {
          _error_res = error.response.data.message;
        } else {
          _error_res = error.response.data.response;
        }
      } else {
        _error_res = error?.response?.data?.message || error.toString();
      }
      return {
        status: "failed",
        message: _error_res,
      };
    }
  };

  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data) {
        const { token } = resp.data;
        dispatch(onSignInSuccess(token));
        if (resp.data.user) {
          dispatch(
            setUser(
              resp.data.user || {
                avatar: "",
                userName: "Anonymous",
                authority: ["USER"],
                email: "",
              }
            )
          );
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
        return {
          status: "success",
          message: "",
        };
      } else {
        return {
          status: "failed",
          message: resp?.message,
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message:
          errors?.response?.data?.message ||
          "Something went wrong, Please try again letter.",
      };
    }
  };
  
 const handleSignOut = useCallback(() => {
    clearAuthTokens();
    resetRefreshState();
    dispatch(onSignOutSuccess());
    dispatch(setUser(initialState));
    navigate(appConfig.unAuthenticatedEntryPath);
  }, [dispatch, navigate]);

  /**
   * Signs out. Revokes the refresh token server-side first so a copy of it is
   * useless afterwards, then clears local state regardless of whether that call
   * succeeded — a network failure must never trap someone in a signed-in UI.
   */
  const signOut = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await apiLogout(refreshToken);
      } catch (e) {
        /* best-effort: the local sign-out below still happens */
      }
    }
    handleSignOut();
  }, [handleSignOut]);

  // NOTE: a 12-hour client-side `login_time` timer used to live here and force a
  // sign-out. It was wrong in both directions — the backend JWT actually lasted
  // 3 hours (so the app kept a dead session for 9 more), and the timer only ran
  // while a tab stayed open. Session lifetime is now the server's decision:
  // the access token is short-lived and silently refreshed, and the refresh
  // token's expiry ends the session.

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
  };
}
export default useAuth;